/**
 * LE DÉCOUPAGE DU DOSSIER RH EN ONGLETS — module PUR (RQ, 06/10/2026).
 *
 * ── Pourquoi ce n'est pas dans le composant ────────────────────────────────
 *
 * Le brouillon de la fiche et la répartition de ses champs entre les onglets
 * sont les deux endroits où une erreur ne se VOIT pas :
 *
 *  · un champ déclaré au brouillon mais rattaché à AUCUN onglet n'est jamais
 *    envoyé — la route répond 200, l'écran dit « Fiche mise à jour », et rien
 *    n'est écrit. C'est exactement le défaut du 26/08/2026 (vingt appels
 *    portant `fonctionIds` acceptés et jamais écrits), côté client cette fois ;
 *  · un champ rattaché à DEUX onglets part depuis deux brouillons qui peuvent
 *    diverger, et le dernier enregistrement gagne — sans que personne l'ait
 *    décidé.
 *
 * Les deux se vérifient par le calcul, à condition que la table soit lisible
 * hors navigateur. D'où ce module sans aucun import d'exécution, et la suite
 * `backend/tests/front-dossier-rh-onglets.test.js` qui le relit.
 *
 * ⚠️ AUCUN IMPORT D'EXÉCUTION ICI, jamais : le lanceur de tests refuse un
 * module front qui en porte un (`compilerFront`). Des types et des constantes,
 * rien d'autre.
 */

/**
 * Le brouillon d'édition du dossier.
 *
 * ⚠️ Les clés portent EXACTEMENT le nom des champs acceptés par
 * `POST /ged/rh/personnel/:orgId`. La route refuse tout champ qu'elle ne
 * connaît pas — elle ne l'ignore plus en répondant 200, ce qui avait laissé
 * vingt appels écrire dans le vide. Renommer une clé ici, c'est donc un refus
 * visible, pas une écriture silencieuse : c'est voulu.
 */
export type Brouillon = {
  /** `nom` = le PATRONYME seul (06/09/2026) ; l'affichage « Prénom Nom » se
   *  compose à la lecture (`nomDe`). ⚠️ LE SEXE N'EST PLUS SAISI NI ENVOYÉ ICI
   *  (RQ, 02/10/2026) : il ne figure donc pas au brouillon — un champ absent du
   *  brouillon n'est pas écrit, la valeur en base reste celle que lisent les
   *  règles d'affectation du planning. */
  nom: string; prenom: string; initiales: string; couleur: string;
  /** Date de naissance `AAAA-MM-JJ` et adresse postale (02/10/2026). */
  dateNaissance: string; adresse: string;
  site: string; orgTier: string; responsableOrgId: string; lienResponsable: string;
  matriculePaie: string; rib: string; banque: string;
  /** Coefficient de garde du récapitulatif comptable (30/09/2026) — texte pour
   *  la saisie (« 4,25 »), vide = à fixer. */
  coefficientGarde: string;
  badgeId: string; email: string; telephone: string;
  dateEntree: string; dateSortie: string;
  /** ⚠️ Booléen et non chaîne : la route lit `!== false`, une chaîne vide
   *  y passerait pour « soumis ». */
  soumisAuPointage: boolean;
  /** Soumis(e) aux jours de congés (30/09/2026) — booléen, même raison. */
  soumisAuxConges: boolean;
  /** Date d'inscription à la badgeuse, `AAAA-MM-JJ` ou vide (29/09/2026). */
  pointageDepuis: string;
  fonctionIds: string[];
  /** Rôles NOMINATIFS (Lot 5, 04/09/2026) — direction/rt/rq/rh, distincts des
   *  droits documentaires. */
  rolesOrg: string[];
  /** Identifiants (`id`) des processus pilotés / suppléés par cette personne —
   *  la MÊME cartographie que `/processus`, jamais une copie (spec §Q5). */
  pilotages: string[];
  suppleances: string[];
};

export const BROUILLON_VIDE: Brouillon = {
  nom: '', prenom: '', initiales: '', couleur: '', dateNaissance: '', adresse: '',
  site: '', orgTier: '', responsableOrgId: '', lienResponsable: 'hierarchique',
  matriculePaie: '', rib: '', banque: '', coefficientGarde: '',
  badgeId: '', email: '', telephone: '',
  dateEntree: '', dateSortie: '', soumisAuPointage: true, soumisAuxConges: true, pointageDepuis: '', fonctionIds: [],
  rolesOrg: [], pilotages: [], suppleances: [],
};

/** Les six rubriques du dossier, dans l'ordre où elles se lisent. */
export type Onglet = 'identite' | 'organisation' | 'contrat' | 'competences' | 'temps' | 'pieces';

/**
 * Les champs du brouillon que chaque onglet modifiable ENVOIE au serveur.
 *
 * ⚠️ CETTE TABLE EST LA DÉCOUPE, et elle doit rester une PARTITION de
 * `BROUILLON_VIDE` : chaque champ dans un onglet, et un seul. Le contrôle le
 * refuse autrement, dans les deux sens — cf. l'en-tête de ce module pour ce que
 * chacun des deux sens coûte.
 *
 * ⚠️ C'est aussi ce découpage qui rend l'enregistrement SÛR : la route
 * `POST ged/rh/personnel/:orgId` est partielle (`if (b.x !== undefined)`), donc
 * un onglet ne peut pas réécrire ce qu'un autre porte avec une valeur lue dix
 * minutes plus tôt. L'ancien envoi global n'avait pas cette propriété.
 */
export const CHAMPS_ONGLET = {
  identite: ['nom', 'prenom', 'initiales', 'couleur', 'dateNaissance', 'adresse', 'email', 'telephone'],
  organisation: ['orgTier', 'site', 'responsableOrgId', 'lienResponsable', 'fonctionIds', 'rolesOrg', 'pilotages', 'suppleances'],
  contrat: ['badgeId', 'matriculePaie', 'coefficientGarde', 'rib', 'banque', 'dateEntree', 'dateSortie',
    'soumisAuPointage', 'soumisAuxConges', 'pointageDepuis'],
} as const satisfies Record<string, readonly (keyof Brouillon)[]>;

export type OngletEditable = keyof typeof CHAMPS_ONGLET;

/** Vrai quand cet onglet porte une saisie (et donc un bouton « Modifier »). */
export const estEditable = (o: Onglet): o is OngletEditable => o in CHAMPS_ONGLET;

/**
 * Les onglets tels que la barre les dessine. `reserve` : rubrique que seuls le
 * gestionnaire RH et le responsable qualité voient — l'onglet reste AFFICHÉ et
 * désactivé, jamais caché : le masquer ferait croire que le dossier n'a que
 * trois rubriques.
 */
export const ONGLETS_DOSSIER: { cle: Onglet; label: string; reserve?: boolean }[] = [
  { cle: 'identite', label: 'Identité' },
  { cle: 'organisation', label: 'Organisation' },
  { cle: 'contrat', label: 'Contrat & administratif', reserve: true },
  { cle: 'competences', label: 'Compétences & formation' },
  { cle: 'temps', label: 'Temps & absences', reserve: true },
  { cle: 'pieces', label: 'Pièces & journal', reserve: true },
];
